"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import {
  subscribeAppearanceSettings,
  applyAppearanceCss,
  applyFavicon,
  DEFAULT_APPEARANCE,
  type AppearanceSettings,
} from "@/services/appearance-settings";

interface BrandingContextValue {
  logoUrl: string;
  faviconUrl: string;
  appName: string;
  logoSlideshow: string[];
  logoSlideshowInterval: number;
  settings: AppearanceSettings;
}

// Hosted in Firebase Storage. Run Super Admin → Tools → Migrate Assets if this is missing.
const DEFAULT_LOGO =
  "https://storage.googleapis.com/breakin-bread-6rr1a.firebasestorage.app/logos/bb-logo.png";

const BrandingContext = createContext<BrandingContextValue>({
  logoUrl: DEFAULT_LOGO,
  faviconUrl: DEFAULT_LOGO,
  appName: "Breakin' Bread",
  logoSlideshow: [],
  logoSlideshowInterval: 6,
  settings: DEFAULT_APPEARANCE,
});

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<AppearanceSettings>(DEFAULT_APPEARANCE);

  useEffect(() => {
    const unsub = subscribeAppearanceSettings((s) => {
      setSettings(s);
      applyAppearanceCss(s);
      const favicon = s.faviconUrl || s.logoUrl;
      if (favicon) applyFavicon(favicon);
    });
    return unsub;
  }, []);

  const logoUrl = settings.logoUrl || DEFAULT_LOGO;
  const faviconUrl = settings.faviconUrl || settings.logoUrl || DEFAULT_LOGO;
  const appName = settings.appName || "Breakin' Bread";
  const logoSlideshow = Array.isArray(settings.logoSlideshow) ? settings.logoSlideshow.filter(Boolean) : [];
  const logoSlideshowInterval = settings.logoSlideshowInterval ?? 6;

  return (
    <BrandingContext.Provider value={{ logoUrl, faviconUrl, appName, logoSlideshow, logoSlideshowInterval, settings }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}
